import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    Box,
    Stack,
    FormControl,
    FormLabel,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { axios } from '@/services/axios';
import {
    Button,
    Heading,
    Input,
    LoadingIndicator,
    Text,
} from '@/components/v4';
import FieldToolTip from '@/components/forms/new/FieldToolTip';
import {
    formLabelStyling,
    stackSpacing,
    stackStyling,
    textBoxStyling,
    breadCrumbStyling,
    headingStyling,
} from '@/components/forms/new/formStyles';
import { customTabStyles, selectedTabStyles } from '@/constants';
import EditTrunkAdminTab from './EditTrunkAdminTab';
import { statusToString } from '../utils';

const EditExtTrunk = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(true);
    const [trunkName, setTrunkName] = useState('');
    const [channels, setChannels] = useState('');
    const [trunkData, setTrunkData] = useState(null);
    // A trunk with no channels is not a trunk, so the floor is 1 even when the
    // trunk type reports 0 (2958)
    const [minChannels, setMinChannels] = useState(1);
    const [incSize, setIncSize] = useState(null);
    const [tabIndex, setTabIndex] = useState(0);
    const [isHoveredName, setIsHoveredName] = useState(false);
    const [isHoveredChannels, setIsHoveredChannels] = useState(false);

    const { darkMode } = useSelector((state) => state.settings);
    const { currentCompany } = useSelector((state) => state.navigationLists);

    const handleBackbutton = () => {
        navigate('/trunks');
        dispatch({
            type: 'CHANGE_TABLE_LOADING',
        });
    };

    useEffect(() => {
        if (id) {
            axios
                .get(`/Trunk/${id}`)
                .then((res) => {
                    setTrunkData(res.data);
                    setTrunkName(res.data.name || '');
                    setChannels(res.data.channels?.toString() || '');
                    setMinChannels(
                        Math.max(1, res.data.trunkType?.minChannels ?? 1),
                    );
                    setIncSize(res.data.trunkType?.incSize);
                    setIsLoading(false);
                })
                .catch((err) => {
                    toast.error(
                        err?.response?.data?.message || 'Failed to load trunk',
                    );
                    setIsLoading(false);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // reset navigation on company change
    useEffect(() => {
        if (trunkData?.companyID && trunkData.companyID !== currentCompany) {
            navigate('/trunks');
        }
    }, [trunkData, currentCompany, navigate]);

    // editing a trunk api call
    const handleEditTrunk = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            await axios.post(
                '/trunk/name',
                {
                    trunkId: trunkData.id,
                    trunkName: trunkName,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                },
            );
            await axios.post(
                '/trunk/channels',
                {
                    trunkId: trunkData.id,
                    channels: parseInt(channels),
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                },
            );
            toast.success('Trunk updated successfully');
            navigate('/trunks');
            dispatch({
                type: 'CHANGE_TABLE_LOADING',
            });
        } catch (err) {
            toast.error(
                err?.response?.data?.message || 'Failed to update trunk',
            );
            setIsLoading(false);
        }
    };

    const breadCrumbTrail = [
        { label: 'Trunks', path: '/trunks' },
        { label: 'Edit Trunk', path: '' },
    ];

    const handleChannelChange = (e) => {
        const value = e.target.value;
        // Only allow non-negative integers
        if (value === '' || /^\d+$/.test(value)) {
            setChannels(value);
        }
    };

    if (isLoading) {
        return <LoadingIndicator />;
    }

    return (
        <>
            <Breadcrumb sx={breadCrumbStyling}>
                {breadCrumbTrail?.map((crumb, index) => (
                    <BreadcrumbItem key={index}>
                        <BreadcrumbLink
                            as={Link}
                            to={crumb.path}
                            onClick={
                                crumb.path === '/trunks'
                                    ? () =>
                                          dispatch({
                                              type: 'CHANGE_TABLE_LOADING',
                                          })
                                    : undefined
                            }>
                            {crumb.label}
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                ))}
            </Breadcrumb>

            <Heading sx={headingStyling}>
                {trunkData?.sipHeader || trunkData?.name}
            </Heading>

            <Tabs index={tabIndex} onChange={setTabIndex} isLazy>
                <TabList borderBottom={0} mb="10px">
                    <Tab sx={customTabStyles} _selected={selectedTabStyles}>
                        General
                    </Tab>
                    <Tab sx={customTabStyles} _selected={selectedTabStyles}>
                        Admin
                    </Tab>
                </TabList>

                <TabPanels>
                    <TabPanel p={0} pt={4}>
                        <Box as="form" onSubmit={handleEditTrunk}>
                            <Box
                                borderRadius={15}
                                padding="30px"
                                maxWidth={895}
                                ml={0}
                                bg={darkMode ? 'dark.tcap' : 'white'}
                                border="1px solid"
                                borderColor={
                                    darkMode
                                        ? 'dark.tcap-borderGray'
                                        : 'gray.200'
                                }>
                                {trunkData?.status !== undefined && (
                                    <FormControl
                                        sx={{ margin: '0 !important', mb: 4 }}>
                                        <Stack
                                            direction="row"
                                            spacing={stackSpacing}
                                            sx={stackStyling()}>
                                            <FormLabel
                                                sx={formLabelStyling(
                                                    darkMode,
                                                    true,
                                                )}>
                                                Status
                                            </FormLabel>
                                            <Box flex={1} maxWidth="400px">
                                                <Text>
                                                    {statusToString(
                                                        trunkData.status,
                                                    )}
                                                </Text>
                                            </Box>
                                        </Stack>
                                    </FormControl>
                                )}

                                <FormControl
                                    sx={{ margin: '0 !important', mb: 4 }}
                                    onMouseEnter={() => setIsHoveredName(true)}
                                    onMouseLeave={() =>
                                        setIsHoveredName(false)
                                    }>
                                    <Stack
                                        direction="row"
                                        spacing={stackSpacing}
                                        sx={stackStyling()}>
                                        <FormLabel
                                            sx={formLabelStyling(
                                                darkMode,
                                                true,
                                            )}>
                                            Trunk Name
                                        </FormLabel>
                                        <Box flex={1} maxWidth="400px">
                                            <Input
                                                type="text"
                                                id="trunkName"
                                                name="trunkName"
                                                value={trunkName}
                                                onChange={(e) =>
                                                    setTrunkName(e.target.value)
                                                }
                                                sx={textBoxStyling}
                                                placeholder="Enter trunk name"
                                            />
                                        </Box>
                                        {isHoveredName && (
                                            <FieldToolTip
                                                label="The display name for this trunk"
                                                ariaLabel="trunk-name-tip"
                                                left="25px"
                                                top="5px"
                                            />
                                        )}
                                    </Stack>
                                </FormControl>

                                <FormControl
                                    sx={{ margin: '0 !important', mb: 4 }}
                                    onMouseEnter={() =>
                                        setIsHoveredChannels(true)
                                    }
                                    onMouseLeave={() =>
                                        setIsHoveredChannels(false)
                                    }>
                                    <Stack
                                        direction="row"
                                        spacing={stackSpacing}
                                        sx={{
                                            ...stackStyling(),
                                            height: 'auto',
                                        }}>
                                        <FormLabel
                                            sx={formLabelStyling(
                                                darkMode,
                                                true,
                                            )}>
                                            Channels
                                        </FormLabel>
                                        <Box flex={1} maxWidth="400px">
                                            <Input
                                                type="text"
                                                id="channels"
                                                name="channels"
                                                value={channels}
                                                onChange={handleChannelChange}
                                                sx={textBoxStyling}
                                                placeholder="Enter number of channels"
                                            />
                                            <Text
                                                fontSize="xs"
                                                color="gray.500"
                                                mt={1}>
                                                Minimum channels is{' '}
                                                {minChannels} in increments of{' '}
                                                {incSize}
                                            </Text>
                                        </Box>
                                        {isHoveredChannels && (
                                            <FieldToolTip
                                                label={`Minimum channels is ${minChannels} in increments of ${incSize}`}
                                                ariaLabel="channels-tip"
                                                left="25px"
                                                top="5px"
                                            />
                                        )}
                                    </Stack>
                                </FormControl>
                            </Box>

                            <Box
                                sx={{
                                    marginTop: '25px',
                                    display: 'flex',
                                    gap: 2,
                                }}>
                                <Button
                                    variant="outline"
                                    sx={{
                                        background: 'white',
                                        minWidth: '120px',
                                    }}
                                    onClick={handleBackbutton}>
                                    Back
                                </Button>
                                <Button
                                    type="submit"
                                    sx={{ minWidth: '120px' }}
                                    isLoading={isLoading}>
                                    Update
                                </Button>
                            </Box>
                        </Box>
                    </TabPanel>

                    <TabPanel p={0} pt={4}>
                        <EditTrunkAdminTab
                            trunkValid={true}
                            trunkName={trunkName}
                        />
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </>
    );
};

export default EditExtTrunk;
